import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Import the default styles
import '../../DatePicker.css'; // Custom styles for the date picker

import ScrollToBottomButton from '../ui/ScrollToBottomButton'; // Import the new component
import Notification from '../ui/Notification'; // Import the Notification component
import heroImage from '../../assets/1.webp';
import heroImageMobile from '../../assets/2.webp';

const Hero = ({ targetId }) => {
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [notification, setNotification] = useState(null);
  const [numAdults, setNumAdults] = useState(1);
  const [numKids, setNumKids] = useState(0);
  const [showGuestSelector, setShowGuestSelector] = useState(false); // State to control guest selector visibility
  const guestSelectorRef = useRef(null); // Ref for guest selector to detect outside clicks
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const checkInRef = useRef(null);
  const checkOutRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (location.state?.notification) {
      setNotification(location.state.notification);
      navigate(location.pathname, { replace: true, state: {} }); // Clear state
    }
  }, [location, navigate]);

  useEffect(() => {
    if (checkInDate && checkOutDate) {
      setShowGuestSelector(true);
    }
  }, [checkInDate, checkOutDate]);

  // Effect to handle clicks outside the guest selector
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (guestSelectorRef.current && !guestSelectorRef.current.contains(event.target)) {
        setShowGuestSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [guestSelectorRef]);

  const openCheckInPicker = () => {
    if (checkInRef.current) {
      checkInRef.current.setOpen(true);
    }
  };

  const openCheckOutPicker = () => {
    if (checkOutRef.current) {
      checkOutRef.current.setOpen(true);
    }
  };

  const CustomDatePickerInput = ({ value, onClick, placeholder }) => (
    <div onClick={onClick} className="val-container">
      <span className="val">{value || placeholder}</span>
    </div>
  );

  const handleBookNowClick = () => {
    if (!checkInDate || !checkOutDate) {
      setNotification("Check-in and check-out should not be empty");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const phoneNumber = '919332730219';
    const message = `Hello, I would like to book a room for ${numAdults} adults and ${numKids} kids from ${checkInDate.toLocaleDateString()} to ${checkOutDate.toLocaleDateString()}.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const totalGuests = numAdults + numKids;
  const currentImage = isMobile ? heroImageMobile : heroImage;
  const heroStyle = {
    backgroundImage: `url(${currentImage})`,
    backgroundPosition: isMobile ? 'center 70%' : 'center',
  };

  const innerStyle = {
    transform: isMobile ? 'translateY(-80px)' : 'none',
  };

  return (
    <section className="hero" id="hero">
      {notification && <Notification message={notification} onClose={() => setNotification(null)} />}
      <div className="hero-bg" style={heroStyle}></div>
      <div className="inner container" style={innerStyle}>
        <h1 data-aos="fade-up">DARJEELING'S BEST LOCATED HERITAGE HOTEL</h1>
        <div className="booking" role="group" aria-label="Booking form" data-aos="fade-up" data-aos-delay="200">
          <div className="field">
            <div className="cal-icon-wrapper" onClick={openCheckInPicker} style={{ position: 'relative', left: '-2.2%' }}>
              {/* calendar icon */}
              <svg className="cal" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="5" width="18" height="16" rx="2" stroke="#0F3D2E" strokeWidth="1.5" /><path d="M8 3v4M16 3v4M3 9h18" stroke="#0F3D2E" strokeWidth="1.5" /></svg>
            </div>
            <div style={{ position: 'relative', left: '-3.3%' }}>
              <span className="meta" style={{ textTransform: 'uppercase', fontWeight: 'bold' }}>check in</span>
              <DatePicker
                ref={checkInRef}
                selected={checkInDate}
                onChange={(date) => setCheckInDate(date)}
                selectsStart
                startDate={checkInDate}
                endDate={checkOutDate}
                minDate={new Date()}
                dateFormat="dd-MM-yyyy"
                placeholderText="SELECT DATE"
                customInput={<CustomDatePickerInput placeholder="SELECT DATE" />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>
          <div className="field">
            <div className="cal-icon-wrapper" onClick={openCheckOutPicker} style={{ position: 'relative', left: '-0.45%' }}>
              <svg className="cal" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="5" width="18" height="16" rx="2" stroke="#0F3D2E" strokeWidth="1.5" /><path d="M8 3v4M16 3v4M3 9h18" stroke="#0F3D2E" strokeWidth="1.5" /></svg>
            </div>
            <div>
              <span className="meta" style={{ textTransform: 'uppercase', fontWeight: 'bold' }}>check out</span>
              <DatePicker
                ref={checkOutRef}
                selected={checkOutDate}
                onChange={(date) => setCheckOutDate(date)}
                selectsEnd
                startDate={checkInDate}
                endDate={checkOutDate}
                minDate={checkInDate || new Date()}
                dateFormat="dd-MM-yyyy"
                placeholderText="SELECT DATE"
                customInput={<CustomDatePickerInput placeholder="SELECT DATE" />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>
          {(checkInDate && checkOutDate) && (
            <div className="field guest-field" ref={guestSelectorRef}>
              <div className="cal-icon-wrapper" onClick={() => setShowGuestSelector(!showGuestSelector)}>
                {/* Users icon */}
                <svg className="cal" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#0F3D2E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="#0F3D2E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M23 21v-2a4 4 0 0 0-3-3.87m-4-12a4 4 0 0 1 0 7.75" stroke="#0F3D2E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div onClick={() => setShowGuestSelector(!showGuestSelector)} style={{ width: '100%' }}>
                <span className="meta" style={{ textTransform: 'uppercase', fontWeight: 'bold' }}>Guests</span>
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <span className="guest-count">{totalGuests} {totalGuests === 1 ? 'Guest' : 'Guests'}</span>
                </div>
              </div>
              {showGuestSelector && (
                <div className="guest-selector-popup">
                  <div className="guest-selector-item">
                    <span>Adults (18+)</span>
                    <div className="guest-selector-controls flex items-center justify-center space-x-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); setNumAdults(Math.max(1, numAdults - 1)); }}
                        className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                      >-</button>
                      <span className="text-xl font-semibold">{numAdults}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setNumAdults(numAdults + 1); }}
                        
                        className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      >+</button>
                    </div>
                  </div>
                  <div className="guest-selector-item">
                    <span>Kids</span>
                    <div className="guest-selector-controls flex items-center justify-center space-x-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); setNumKids(Math.max(0, numKids - 1)); }}
                        className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                      >-</button>
                      <span className="text-xl font-semibold">{numKids}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setNumKids(numKids + 1); }}
                        
                        className="w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      >+</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <button className="cta" onClick={handleBookNowClick}>BOOK NOW</button>
        </div>
      </div>
      
    <ScrollToBottomButton targetId={targetId} />
    </section>
  );
};

export default Hero;